/**
 * WP-33 / MR-03 — WHERE THE FIFTH DESTINATION ONWARDS LIVES ON A PHONE.
 *
 * Owner ruling R-13 caps the fixed bottom bar at four destinations. The rest are
 * not removed and are not hidden behind a scroll nobody can see: they move here,
 * behind one labelled control, as a bottom sheet.
 *
 * A SHEET RATHER THAN A POPOVER because the trigger sits on the very bottom edge
 * of the screen — a popover would open upward over the content it was launched
 * from, and on a 320px-wide phone it would be the same nine-item strip in a
 * narrower box. The sheet gets the full width and a real 44px row per item.
 *
 * PORTALLED, and that is not optional: the bar is `position: fixed` with
 * `overflow: hidden` and a `backdrop-filter`, which makes it both a containing
 * block and a stacking context. Rendered inside it, this sheet would be clipped
 * to 72px — the exact failure that made the portfolio hint undismissable.
 *
 * The focus trap, the Escape handling and the body scroll lock all come from
 * `useDialogFocus`, which is the app's shared answer and owns the nested-dialog
 * stack. Nothing about this layer is special enough to justify its own.
 */
import React, { useEffect } from 'react';
import { ModalPortal } from '../../../modal/modal-portal';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import {
  NavSheet,
  NavSheetClose,
  NavSheetHead,
  NavSheetItem,
  NavSheetScrim,
} from '../dashboard.styled';
import { Text, font } from '../../../../style-utils/text';
import { palette } from '../../../../style-utils/theme';
import { Presence, overlayMotion, scrimMotion } from '../../../../motion';

export interface NavSheetEntry {
  label: string;
  slug: string;
}

interface NavMoreSheetProps {
  open: boolean;
  entries: NavSheetEntry[];
  /** The section the reader is currently on, if it is one of these. */
  currentSlug: string;
  onSelect: (slug: string) => void;
  onClose: () => void;
}

export const NavMoreSheet: React.FC<NavMoreSheetProps> = ({
  open,
  entries,
  currentSlug,
  onSelect,
  onClose,
}) => {
  const sheetRef = useDialogFocus<HTMLDivElement>(open, onClose);

  /* A BOTTOM SHEET IS A PHONE LAYER, and the control that opens it does not
     exist above md. Without this, widening the window (or rotating a tablet)
     leaves a sheet on screen that nothing on that screen can explain — and, if
     it were merely hidden with a media query, an invisible dialog would still be
     holding the focus trap. Closing it is the honest resolution. */
  useEffect(() => {
    if (!open) return undefined;

    window.addEventListener('resize', onClose);

    return () => window.removeEventListener('resize', onClose);
  }, [open, onClose]);

  /* THE PRESENCE LIVES IN HERE, so the caller keeps its plain `open` prop and
     the sheet still animates OUT: the portal stays mounted for the exit and is
     removed once the scrim's fade and the sheet's drop have both finished. The
     presence signal is React context, which crosses the portal. */
  return (
    <Presence>
      {open ? (
        <ModalPortal key="nav-more">
      <NavSheetScrim
        {...scrimMotion}
        data-testid="nav-more-scrim"
        onClick={onClose}
      />
      <NavSheet
        {...overlayMotion('sheet')}
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="More destinations"
        data-testid="nav-more-sheet"
        tabIndex={-1}
      >
        <NavSheetHead>
          <Text
            as="h2"
            mt={0}
            mb={0}
            fontFamily={font.family}
            fontSize={font.size.xxs}
            fontWeight={600}
            color={palette.slateLight}
            style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
          >
            More
          </Text>
          {/* data-autofocus keeps the landing point on the way OUT rather than on
              the first destination — see the note in use-dialog-focus.tsx. */}
          <NavSheetClose
            type="button"
            data-autofocus
            data-testid="nav-more-close"
            onClick={onClose}
          >
            Close
          </NavSheetClose>
        </NavSheetHead>
        {entries.map(entry => (
          <NavSheetItem
            key={entry.slug}
            type="button"
            active={entry.slug === currentSlug}
            aria-current={entry.slug === currentSlug ? 'page' : undefined}
            onClick={() => onSelect(entry.slug)}
          >
            {entry.label}
          </NavSheetItem>
        ))}
      </NavSheet>
        </ModalPortal>
      ) : null}
    </Presence>
  );
};
